import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Globe, Zap, ArrowRight, Lock, Search, Database, FileCheck, Code2, Moon, Sun, BookOpen, Clock } from 'lucide-react';
import { TrendingUp, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { FloatingFeaturedBadge } from './FloatingV2Badge';

const LandingPage = () => {
  const schoolLogo = "https://customer-assets.emergentagent.com/job_hoaxradar/artifacts/h5pnk3as_R.png";
  const appLogo = "https://customer-assets.emergentagent.com/job_hoaxradar/artifacts/ibx3jhv6_627017088_926588633181586_494122033917834643_n.jpg";
  const schoolLink = "https://malang.tarunanusantara.sch.id/";

  const [darkMode, setDarkMode] = useState(false);

  const [trending, setTrending] = useState({ hoaxes: [], news: [] });
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        setFetchError(null);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
        const res = await axios.get(`${baseUrl}/api/analysis/trending?language=en`);
        setTrending({ hoaxes: res.data.trending_hoaxes || [], news: res.data.hot_news || [] });
      } catch (err) {
        console.error("Failed to fetch trending:", err);
        setFetchError(err.message || "Gagal mengambil data dari server");
      } finally {
        setLoadingTrending(false);
      }
    };

    fetchTrending();

    // Auto-refresh every 24 hours (86400000 ms) to save API costs
    const intervalId = setInterval(fetchTrending, 86400000);

    return () => clearInterval(intervalId);
  }, []);
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  const developers2 = [
    { name: "Johannes Sitanggang", class: "XIAERO" },
    { name: "Sambada Dharma Manggala", class: "XIAERO" },
    { name: "Antonius Vian Yogathama", class: "XIA4" },
    { name: "Idham Firwandi", class: "XIA4" },
    { name: "Khiar Langit Ramadhan", class: "XIC2" },
    { name: "Jack William Sinaga", class: "XIAERO" }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 font-sans selection:bg-primary/10 selection:text-primary scroll-smooth transition-colors duration-300">
        {/* Navbar */}
        <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-gray-950/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 transition-all duration-300">
            <div className="container mx-auto px-6 h-20 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <img src={appLogo} alt="VeryTrust Logo" className="w-10 h-10 rounded-full object-cover shadow-sm border border-gray-200 dark:border-gray-700" />
                    <span className="text-xl font-heading font-bold text-gray-900 dark:text-white tracking-tight">VeryTrust</span>
                </div>
                
                <div className="hidden md:flex gap-8 text-sm font-medium text-gray-500 dark:text-gray-400">
                    <Link to="/education" className="hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-1">
                        <BookOpen className="w-4 h-4" /> Education
                    </Link>
                    <Link to="/plagiarism" className="hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-1">
                        <FileCheck className="w-4 h-4" /> Plagiarism Detector
                    </Link>
                    <Link to="/history" className="hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-1">
                        <Clock className="w-4 h-4" /> History
                    </Link>
                    <a href="#roadmap" className="hover:text-primary dark:hover:text-primary transition-colors">Roadmap</a>
                    <a href="#developer" className="hover:text-primary dark:hover:text-primary transition-colors">Developer Team</a>
                </div>

                <div className="flex items-center gap-4">
                    <button 
                        onClick={toggleTheme}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400"
                    >
                        {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </button>

                    <Link to="/app">
                        <button className="hidden sm:block px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-medium text-sm rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 transition-all shadow-lg shadow-gray-200 dark:shadow-none">
                            Launch App
                        </button>
                    </Link>
                    {/* School/Identity Logo */}
                    <div className="border-l border-gray-200 dark:border-gray-700 pl-4">
                         <a href={schoolLink} target="_blank" rel="noopener noreferrer">
                            <img src={schoolLogo} alt="School Identity" className="w-12 h-12 object-contain hover:scale-110 transition-transform cursor-pointer" />
                         </a>
                    </div>
                </div>
            </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-32 pb-20 md:pt-48 md:pb-32 container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center space-y-8">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-semibold uppercase tracking-wide border border-blue-100 dark:border-blue-800"
                >
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                    </span>
                    Live Hoax Detection Protocol
                </motion.div>
                
                <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-5xl md:text-7xl font-heading font-extrabold text-gray-900 dark:text-white leading-[1.1] tracking-tight"
                >
                    Trust, but <span className="text-primary">verify</span> everything.
                </motion.h1>

                <motion.p 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed"
                >
                    The student-led initiative to combat misinformation. Powered by advanced AI and global consensus mapping to neutralize hoaxes instantly.
                </motion.p>

                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="flex flex-col sm:flex-row justify-center gap-4 pt-4"
                >
                    <Link to="/app">
                        <button className="px-8 py-4 bg-primary text-white font-semibold text-lg rounded-xl shadow-xl shadow-blue-200 dark:shadow-blue-900/20 hover:shadow-2xl hover:-translate-y-1 transition-all flex items-center justify-center gap-2 w-full sm:w-auto">
                            Start Verification <ArrowRight className="w-5 h-5" />
                        </button>
                    </Link>
                    <Link to="/education">
                        <button className="px-8 py-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 font-semibold text-lg rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all w-full sm:w-auto flex items-center justify-center gap-2">
                            <BookOpen className="w-5 h-5" /> Learn to Spot Hoaxes
                        </button>
                    </Link>
                </motion.div>
            </div>
        </section>

        {/* Plagiarism Detection Section - NEW */}
        <section className="py-20 bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
            <div className="container mx-auto px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        {/* Left: Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-6"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold uppercase tracking-wide">
                                <FileCheck className="w-3 h-3" />
                                Writer's Toolkit
                            </div>
                            
                            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 dark:text-white leading-tight">
                                Check Plagiarism in Seconds
                            </h2>
                            
                            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                                Comprehensive plagiarism detection suite for students, writers, and researchers. 
                                Verify originality, detect AI-generated content, and compare documents instantly.
                            </p>

                            {/* Feature highlights */}
                            <div className="space-y-3 pt-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Search className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white">Web Plagiarism Scanner</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Search for copied content across billions of web pages</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Zap className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white">AI Content Detector</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Identify text written by ChatGPT, Gemini, or other AI tools</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Database className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white">Document Comparison</h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Upload multiple files and find similarities instantly</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4">
                                <Link to="/plagiarism">
                                    <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold text-lg rounded-xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2">
                                        <FileCheck className="w-5 h-5" />
                                        Check Plagiarism Now
                                        <ArrowRight className="w-5 h-5" />
                                    </button>
                                </Link>
                            </div>
                        </motion.div>

                        {/* Right: Visual/Stats */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-100 dark:border-gray-700">
                                {/* Mock Detection Result */}
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Plagiarism Score</span>
                                        <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full font-semibold">Original</span>
                                    </div>
                                    
                                    <div className="relative">
                                        <div className="flex items-center justify-center">
                                            <div className="relative w-32 h-32">
                                                <svg className="w-full h-full transform -rotate-90">
                                                    <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" className="text-gray-200 dark:text-gray-700" />
                                                    <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="none" strokeDasharray="351.86" strokeDashoffset="315" className="text-green-500 transition-all duration-1000" strokeLinecap="round" />
                                                </svg>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-3xl font-bold text-gray-900 dark:text-white">8%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pt-4 space-y-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                            <span className="text-sm text-gray-600 dark:text-gray-300">No plagiarism detected</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                            <span className="text-sm text-gray-600 dark:text-gray-300">Human-written content</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                            <span className="text-sm text-gray-600 dark:text-gray-300">3 sources checked</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating badge */}
                            <div className="absolute -top-4 -right-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full shadow-lg">
                                <span className="text-xs font-bold">3-in-1 Tool</span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>

        {/* Feature Grid */}
        <section id="features" className="py-24 bg-gray-50 dark:bg-gray-900/50">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-4">Core Capabilities</h2>
                    <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">Built with state-of-the-art technology to ensure you never fall for fake news again.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition-shadow group">
                        <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6 group-hover:scale-110 transition-transform">
                            <Globe className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Global Consensus</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                            Cross-references claims against thousands of verified international sources in 50+ languages.
                        </p>
                    </div>
                    <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition-shadow group">
                        <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center justify-center text-green-600 dark:text-green-400 mb-6 group-hover:scale-110 transition-transform">
                            <Zap className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Real-time Analysis</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                            Sub-second latency verification engine powered by advanced edge-inference LLMs.
                        </p>
                    </div>
                    <div className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition-shadow group">
                        <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center text-purple-600 dark:text-purple-400 mb-6 group-hover:scale-110 transition-transform">
                            <Lock className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Unbiased Scoring</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                            Transparent "Trust Score" algorithm that eliminates cultural and political bias.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        {/* Trending & Hot News Section */}
        <section className="py-16 bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800">
            <div className="container mx-auto px-6">
                <div className="grid md:grid-cols-2 gap-12">
                    {/* Trending Hoaxes */}
                    <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl p-8 border border-red-100 dark:border-red-900/30">
                        <div className="flex items-center gap-3 mb-6">
                            <AlertTriangle className="w-6 h-6 text-red-500" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Trending Hoaxes</h3>
                        </div>
                        {loadingTrending ? (
                            <div className="space-y-4 animate-pulse">
                                {[1,2,3].map(i => <div key={i} className="h-16 bg-red-100 dark:bg-red-900/20 rounded-xl" />)}
                            </div>
                        ) : fetchError ? (
                            <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-xl text-sm font-medium">
                                Error: {fetchError} <br />
                                (Pastikan API Key sudah benar dan backend telah di-redeploy)
                            </div>
                        ) : trending.hoaxes.length === 0 ? (
                            <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-xl text-sm font-medium">
                                Data hoaks kosong. Pastikan kuota SERPER_API_KEY Anda masih tersedia.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {trending.hoaxes.slice(0, 5).map((hoax, idx) => (
                                    <div key={idx} className="bg-white dark:bg-gray-900 p-4 rounded-xl shadow-sm border border-red-100 dark:border-red-900/30 hover:shadow-md transition-shadow">
                                        <a href={hoax.link} target="_blank" rel="noopener noreferrer" className="text-gray-900 dark:text-gray-100 font-semibold hover:text-red-600 dark:hover:text-red-400 transition-colors line-clamp-2">
                                            {hoax.title}
                                        </a>
                                        <p className="text-sm text-gray-500 mt-2 line-clamp-1">{hoax.snippet}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Hot News like Twitter */}
                    <div className="bg-blue-50 dark:bg-blue-900/10 rounded-2xl p-8 border border-blue-100 dark:border-blue-900/30">
                        <div className="flex items-center gap-3 mb-6">
                            <TrendingUp className="w-6 h-6 text-blue-500" />
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Hot News (Global & Local)</h3>
                        </div>
                        {loadingTrending ? (
                            <div className="space-y-4 animate-pulse">
                                {[1,2,3].map(i => <div key={i} className="h-16 bg-blue-100 dark:bg-blue-900/20 rounded-xl" />)}
                            </div>
                        ) : fetchError ? (
                            <div className="p-4 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-xl text-sm font-medium">
                                Error: {fetchError} <br />
                                (Pastikan API Key sudah benar dan backend telah di-redeploy)
                            </div>
                        ) : trending.news.length === 0 ? (
                            <div className="p-4 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-xl text-sm font-medium">
                                Data berita kosong. Pastikan kuota SERPER_API_KEY Anda masih tersedia.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {trending.news.slice(0, 5).map((newsItem, idx) => (
                                    <div key={idx} className="bg-white dark:bg-gray-900 p-4 rounded-xl shadow-sm border border-blue-100 dark:border-blue-900/30 hover:shadow-md transition-shadow flex flex-col justify-between">
                                        <a href={newsItem.link} target="_blank" rel="noopener noreferrer" className="text-gray-900 dark:text-gray-100 font-semibold hover:text-blue-600 dark:hover:text-blue-400 transition-colors line-clamp-2">
                                            {newsItem.title}
                                        </a>
                                        <div className="flex justify-between items-center mt-3">
                                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-md">{newsItem.source || 'News'}</span>
                                            <span className="text-xs text-gray-500">{newsItem.date || 'Baru Saja'}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>

        {/* How It Works (Visual Tutorial) */}
        <section id="how-it-works" className="py-24 bg-white dark:bg-gray-950 relative overflow-hidden">
             {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-blue-50 via-transparent to-transparent dark:from-blue-900/10 opacity-50" />
            
            <div className="container mx-auto px-6 relative z-10">
                <div className="text-center mb-20">
                    <span className="text-primary font-semibold text-sm uppercase tracking-widest">Tutorial</span>
                    <h2 className="text-4xl font-heading font-bold text-gray-900 dark:text-white mt-2 mb-4">How to Verify Truth</h2>
                    <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">Three simple steps to separate fact from fiction using VeryTrust.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
                    {/* Connecting Line (Desktop) */}
                    <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-gray-200 via-primary/50 to-gray-200 dark:from-gray-800 dark:to-gray-800 z-0" />

                    {/* Step 1 */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="relative z-10 flex flex-col items-center text-center"
                    >
                        <div className="w-24 h-24 bg-white dark:bg-gray-900 rounded-full border-4 border-blue-50 dark:border-blue-900/30 flex items-center justify-center shadow-lg mb-8 relative">
                            <Search className="w-10 h-10 text-primary" />
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center text-white dark:text-gray-900 font-bold text-sm border-4 border-white dark:border-gray-900">1</div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Input Claim</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                            Paste a URL, type a news headline, or enter a statement you suspect might be false into the search bar.
                        </p>
                    </motion.div>

                    {/* Step 2 */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="relative z-10 flex flex-col items-center text-center"
                    >
                        <div className="w-24 h-24 bg-white dark:bg-gray-900 rounded-full border-4 border-blue-50 dark:border-blue-900/30 flex items-center justify-center shadow-lg mb-8 relative">
                            <Database className="w-10 h-10 text-purple-500" />
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center text-white dark:text-gray-900 font-bold text-sm border-4 border-white dark:border-gray-900">2</div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">AI Scanning</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                            Our AI cross-references your input against millions of trusted sources and internal knowledge bases in real-time.
                        </p>
                    </motion.div>

                    {/* Step 3 */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 }}
                        className="relative z-10 flex flex-col items-center text-center"
                    >
                        <div className="w-24 h-24 bg-white dark:bg-gray-900 rounded-full border-4 border-blue-50 dark:border-blue-900/30 flex items-center justify-center shadow-lg mb-8 relative">
                            <FileCheck className="w-10 h-10 text-green-500" />
                            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-900 dark:bg-white rounded-full flex items-center justify-center text-white dark:text-gray-900 font-bold text-sm border-4 border-white dark:border-gray-900">3</div>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Receive Verdict</h3>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                            Get an instant Trust Score (0-100%) and a detailed explanation of why the content is true, false, or misleading.
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>

        {/* Developer Team Section - Featured Contributors */}
        <section id="developer" className="py-24 bg-gray-900 dark:bg-black text-white">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-pink-500/10 rounded-full border border-purple-500/30 mb-4">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 font-bold text-sm uppercase tracking-widest">
                            ✨ Featured Team
                        </span>
                    </div>
                    <h2 className="text-4xl font-heading font-bold text-white mt-2 mb-4">VeryTrust Contributors</h2>
                    <p className="text-gray-400 max-w-xl mx-auto">The student minds behind VeryTrust platform.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    {developers2.map((dev, idx) => (
                        <motion.div 
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            className="relative bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-transparent hover:border-purple-500/50 rounded-xl p-6 text-center group overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                            
                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                    <Code2 className="w-8 h-8 text-purple-400" />
                                </div>
                                <h4 className="text-lg font-bold text-white mb-1">{dev.name}</h4>
                                <p className="text-sm text-purple-400 font-semibold">{dev.class}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>

        {/* Roadmap Section - Replace Video */}
        <section id="roadmap" className="py-24 bg-white dark:bg-gray-950">
            <div className="container mx-auto px-6">
                <div className="text-center mb-16">
                    <span className="text-primary font-semibold text-sm uppercase tracking-widest">Future Vision</span>
                    <h2 className="text-4xl font-heading font-bold text-gray-900 dark:text-white mt-2 mb-4">Development Roadmap</h2>
                    <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">Our plan to make VeryTrust the most comprehensive verification platform.</p>
                </div>

                <div className="max-w-4xl mx-auto">
                    {/* Current Phase */}
                    <div className="mb-12">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Current Phase - Q1 2026</h3>
                        </div>
                        <div className="grid gap-4 pl-6 border-l-2 border-green-500">
                            <div className="bg-green-50 dark:bg-green-900/10 p-4 rounded-lg border border-green-200 dark:border-green-800">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">✅ Hoax DNA Fingerprinting</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Track hoax evolution with unique fingerprints</p>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/10 p-4 rounded-lg border border-green-200 dark:border-green-800">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">✅ Plagiarism Detection Suite</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Web search, document comparison, AI detection</p>
                            </div>
                        </div>
                    </div>

                    {/* Next Phase */}
                    <div className="mb-12">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Next Up - Q2 2026</h3>
                        </div>
                        <div className="grid gap-4 pl-6 border-l-2 border-blue-500">
                            <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">🎥 Advanced Media Verification</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Image, video, and audio deepfake detection</p>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">🔗 Cross-Article Contradiction Detector</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Find contradicting claims across multiple articles</p>
                            </div>
                        </div>
                    </div>

                    {/* Future Vision */}
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Long-term Vision - 2026+</h3>
                        </div>
                        <div className="grid gap-4 pl-6 border-l-2 border-purple-500">
                            <div className="bg-purple-50 dark:bg-purple-900/10 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">👤 User Authentication & Cloud History</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Persistent accounts with synced history across devices</p>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/10 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">💰 Developer API & Monetization</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Public API for third-party integrations</p>
                            </div>
                            <div className="bg-purple-50 dark:bg-purple-900/10 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">🌐 Browser Extension</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Real-time fact-checking as you browse</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* Footer */}
        <footer className="bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 py-12">
            <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-2">
                    <img src={appLogo} alt="Logo" className="w-6 h-6 rounded-full grayscale opacity-50" />
                    <span className="font-heading font-bold text-gray-400 dark:text-gray-600">VeryTrust</span>
                </div>
                {/* Copyright removed as requested */}
            </div>
        </footer>
        
        {/* Floating Featured Badge */}
        <FloatingFeaturedBadge />
    </div>
  );
};

export default LandingPage;
